import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IntakeFormData } from '../lib/types/domains/intake-form';
import { formSections } from '../lib/config/formConfig';
import { useToast } from '../contexts/ToastContext';
import { validateSection } from '../lib/validators/intake-form';
import { IntakeFormProvider, useIntakeForm } from '../contexts/IntakeFormContext';
import { debounce } from '../lib/utils/debounce';
import { useThemeClasses } from '../lib/themes/utils';
import { useAuth } from "../hooks/useSupabaseAuth"
import { API_ENDPOINTS, getAuthHeaders, shouldIncludeCredentials } from '../lib/config/api-endpoints';


const MultiStepIntakeFormContent: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const theme = useThemeClasses();
  const { 
    formData, 
    currentStep, 
    setStep, 
    setSection, 
    isSubmitting, 
    setSubmitting,
    updateField 
  } = useIntakeForm();
  
  const { user, client } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [sessionStartTime] = useState(Date.now());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const abandonmentTrackingRef = useRef<NodeJS.Timeout | null>(null);

  // Load saved data on mount
  useEffect(() => {
    if (client?.intake_data) {
      setSection(client.intake_data);
      
      // Find last completed section
      const completedSections = Object.keys(client.intake_data || {});
      if (completedSections.length > 0) {
        const lastCompletedIndex = formSections.findIndex(s => 
          completedSections.includes(s.dataKey || '')
        );
        if (lastCompletedIndex >= 0) {
          setStep(Math.min(lastCompletedIndex + 1, formSections.length - 1));
        }
      }
    }
  }, [client, setSection, setStep]);

  // Auto-save functionality with debouncing
  const autoSaveData = useCallback(
    debounce(async (sectionId: string, data: any) => {
      // Use current values instead of stale closure values
      if (!client?.id || isSubmitting) return;
      
      setIsSaving(true);
      setHasUnsavedChanges(false);
      
      try {
        const response = await fetch(API_ENDPOINTS.clients.saveIntakeData, {
          method: 'PUT', // PUT for auto-save
          headers: getAuthHeaders(API_ENDPOINTS.clients.saveIntakeData),
          credentials: shouldIncludeCredentials(API_ENDPOINTS.clients.saveIntakeData) ? 'include' : 'same-origin',
          body: JSON.stringify({
            section: sectionId,
            data,
            isAutoSave: true
          })
        });

        if (response.ok) {
          setLastSavedAt(new Date());
          // Also save to localStorage as backup
          localStorage.setItem('intake-form-draft', JSON.stringify({
            section: sectionId,
            data,
            timestamp: new Date().toISOString()
          }));
        } else {
          console.error('Auto-save failed:', await response.text());
          // Fall back to localStorage
          localStorage.setItem('intake-form-draft', JSON.stringify({
            section: sectionId,
            data,
            timestamp: new Date().toISOString()
          }));
        }
      } catch (error) {
        console.error('Auto-save error:', error);
      } finally {
        setIsSaving(false);
      }
    }, 2000), // 2 second debounce
    [] // Remove all dependencies to prevent infinite loops
  );

  // Track field changes for auto-save
  useEffect(() => {
    if (hasUnsavedChanges && !isSubmitting) {
      const section = formSections[currentStep];
      const sectionData = formData[section.dataKey as keyof IntakeFormData];
      
      if (sectionData && section.dataKey) {
        autoSaveData(section.id, { [section.dataKey]: sectionData });
      }
    }
  }, [formData, currentStep, hasUnsavedChanges, isSubmitting]); // Remove autoSaveData from dependencies to prevent infinite loop

  // Set up abandonment tracking
  useEffect(() => {
    const trackAbandonment = () => {
      const clientId = client?.id || user?.clientId;
      if (!clientId) return;
      
      const timeOnForm = Math.floor((Date.now() - sessionStartTime) / 1000);
      const section = formSections[currentStep];
      
      // Use sendBeacon for reliability
      if (navigator.sendBeacon) {
        const params = new URLSearchParams({
          clientId: clientId,
          lastSection: section.id,
          timeOnForm: timeOnForm.toString()
        });
        navigator.sendBeacon(`/api/clients/track-abandonment?${params}`);
      } else {
        // Fallback to fetch
        // Track abandonment endpoint not migrated to Supabase yet
        // TODO: Create Supabase edge function for this
        console.log('Track abandonment (page unload):', {
          clientId,
          lastSection: section.id,
          timeOnFormSeconds: timeOnForm
        });
      }
    };

    // Track on page unload
    window.addEventListener('beforeunload', trackAbandonment);
    
    // Track after 5 minutes of inactivity
    const resetInactivityTimer = () => {
      if (abandonmentTrackingRef.current) {
        clearTimeout(abandonmentTrackingRef.current);
      }
      
      abandonmentTrackingRef.current = setTimeout(() => {
        const clientId = client?.id || user?.clientId;
      if (clientId && !isSubmitting) {
          // Track abandonment endpoint not migrated to Supabase yet
          // TODO: Create Supabase edge function for this
          console.log('Track abandonment (inactivity):', {
            clientId,
            lastSection: formSections[currentStep].id,
            timeOnFormSeconds: Math.floor((Date.now() - sessionStartTime) / 1000)
          }).catch(() => {});
        }
      }, 5 * 60 * 1000); // 5 minutes
    };

    // Reset timer on any interaction
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });
    
    resetInactivityTimer();

    return () => {
      window.removeEventListener('beforeunload', trackAbandonment);
      events.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
      if (abandonmentTrackingRef.current) {
        clearTimeout(abandonmentTrackingRef.current);
      }
    };
  }, [client?.id, user?.clientId, currentStep, sessionStartTime, isSubmitting]);

  // Handle field updates
  const handleFieldUpdate = useCallback((path: string, value: any) => {
    updateField(path, value);
    setHasUnsavedChanges(true);
  }, [updateField]);

  // Save section data
  const saveSection = async (isAutoSave = false) => {
    if (!client?.id) return false;
    
    const section = formSections[currentStep];
    let dataToSave: any = {};
    
    // Build data based on section
    if (section.id === 'personalInfo') {
      dataToSave = {
        personalInfo: formData.personalInfo,
        practitionerQuestions: formData.practitionerQuestions
      };
    } else if (section.id === 'pastHealthMedicalHistory') {
      dataToSave = {
        preExistingConditions: formData.preExistingConditions,
        familyMedicalHistory: formData.familyMedicalHistory
      };
    } else {
      const sectionKey = section.dataKey || section.id;
      dataToSave = {
        [sectionKey]: formData[sectionKey as keyof IntakeFormData]
      };
    }
    
    try {
      setSubmitting(true);
      const response = await fetch(API_ENDPOINTS.clients.saveIntakeData, {
        method: 'POST',
        headers: getAuthHeaders(API_ENDPOINTS.clients.saveIntakeData),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.clients.saveIntakeData) ? 'include' : 'same-origin',
        body: JSON.stringify({
          section: section.id,
          data: dataToSave,
          isAutoSave
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save');
      }
      
      // TODO: Use result for validation if needed
      
      setHasUnsavedChanges(false);
      if (!isAutoSave) {
        showSuccess('Progress saved');
      }
      return true;
    } catch (error) {
      if (!isAutoSave) {
        showError('Failed to save progress. Please try again.');
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    // Validate current section
    const section = formSections[currentStep];
    
    if (section.id === 'personalInfo') {
      const personalInfoValidation = validateSection('personalInfo', formData.personalInfo || {});
      const practitionerValidation = validateSection('practitionerQuestions', formData.practitionerQuestions || {});
      
      if (!personalInfoValidation.success || !practitionerValidation.success) {
        // Show specific field errors
        const errors = [
          ...(personalInfoValidation.errors || []),
          ...(practitionerValidation.errors || [])
        ];
        if (errors.length > 0) {
          showError(`Please complete: ${errors.map(e => e.field).join(', ')}`);
        } else {
          showError('Please complete all required fields');
        }
        return;
      }
    } else if (section.id !== 'pastHealthMedicalHistory') {
      const sectionKey = section.dataKey || section.id;
      const sectionData = formData[sectionKey as keyof IntakeFormData] || {};
      const validation = validateSection(sectionKey, sectionData);
      
      if (!validation.success) {
        // Show specific field errors
        if (validation.errors && validation.errors.length > 0) {
          showError(`Please complete: ${validation.errors.map(e => e.field).join(', ')}`);
        } else {
          showError('Please complete all required fields');
        }
        return;
      }
    }
    
    // Save current section
    const saved = await saveSection();
    if (!saved) return;
    
    if (currentStep < formSections.length - 1) {
      setStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      // Final submission
      handleFinalSubmit();
    }
  };

  const handleFinalSubmit = async () => {
    try {
      setSubmitting(true);
      
      const response = await fetch(API_ENDPOINTS.clients.submitIntake, {
        method: 'POST',
        headers: getAuthHeaders(API_ENDPOINTS.clients.submitIntake),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.clients.submitIntake) ? 'include' : 'same-origin'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Submission failed');
      }
      
      const result = await response.json();
      showSuccess('Form submitted successfully!');
      
      // Navigate to success page with booking info
      navigate('/submission-success', { 
        state: { 
          clientId: result.clientId || client?.id,
          bookingUrl: result.nextSteps?.bookingUrl,
          clientName: client?.first_name || user?.name,
          clientEmail: client?.email || user?.email
        } 
      });
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepClick = async (stepIndex: number) => {
    // Save current section first if moving to a different step
    if (stepIndex !== currentStep && hasUnsavedChanges) {
      await saveSection(true);
    }
    
    // Allow navigation to any previously visited step
    const targetSection = formSections[stepIndex];
    const sectionData = formData[targetSection.dataKey as keyof IntakeFormData];
    const hasData = sectionData && Object.keys(sectionData).length > 0;
    
    if (stepIndex <= currentStep || hasData) {
      setStep(stepIndex);
      window.scrollTo(0, 0);
    }
  };


  const CurrentSection = formSections[currentStep].component;
  // const currentSectionData = formSections[currentStep];
  // Calculate progress based on form data
  const completedSectionsData = formSections.filter(section => {
    const sectionKey = section.dataKey || section.id;
    const sectionData = formData[sectionKey as keyof IntakeFormData];
    return sectionData && Object.keys(sectionData).length > 0;
  });
  const completedSections = completedSectionsData.map(section => section.id);
  const progress = Math.round((completedSectionsData.length / formSections.length) * 100);

  return (
    <div className={`min-h-screen ${theme.backgrounds.gradient}`}>
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header with Progress */}
        <div className={`${theme.cards.elevated} mb-6 sm:mb-8 p-6`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-2xl sm:text-3xl ${theme.typography.heading}`}>
              Your Wellness Journey
            </h2>
            <div className="text-right">
              <span className={`text-sm ${theme.text.neutralLight} block`}>Progress</span>
              <span className={`text-2xl ${theme.text.primary} font-semibold`}>
                {progress}%
              </span>
            </div>
          </div>
          
          {/* Enhanced Progress Bar */}
          <div className="relative mb-6">
            <div className={`h-3 ${theme.backgrounds.neutralCard} ${theme.radius.full} overflow-hidden`}>
              <div 
                className={`h-full ${theme.backgrounds.gradientPrimary} transition-all duration-700 ease-out relative overflow-hidden`}
                style={{ width: `${progress}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
              </div>
            </div>
            
            {/* Step indicators */}
            <div className="absolute inset-0 flex items-center">
              {formSections.map((section, index) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => handleStepClick(index)}
                  className={`
                    absolute w-8 h-8 -mt-2.5 transform -translate-x-1/2 
                    ${theme.radius.full} ${theme.shadows.small}
                    transition-all ${theme.animations.duration}
                    ${index === currentStep
                      ? `${theme.backgrounds.gradientPrimary} scale-110 ring-4 ring-primary-200`
                      : completedSections.includes(section.id)
                      ? `${theme.backgrounds.primary} cursor-pointer hover:scale-110`
                      : 'bg-white border-2 ' + theme.borders.neutral
                    }
                  `}
                  disabled={!completedSections.includes(section.id) && index > currentStep}
                  title={section.id.replace(/([A-Z])/g, ' $1').trim()}
                  style={{ left: `${(index / (formSections.length - 1)) * 100}%` }}
                >
                  {completedSections.includes(section.id) && index !== currentStep && (
                    <svg className="w-4 h-4 text-white mx-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                  {index === currentStep && (
                    <div className="w-2 h-2 bg-white rounded-full mx-auto"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
          
          {/* Current step info */}
          <div className={`text-center ${theme.text.neutral}`}>
            <span className="font-medium">
              {formSections[currentStep].id.replace(/([A-Z])/g, ' $1').trim()}
            </span>
            <span className={`mx-2 ${theme.text.neutralLight}`}>•</span>
            <span className={theme.text.neutralLight}>
              Step {currentStep + 1} of {formSections.length}
            </span>
          </div>
        </div>

        {/* Auto-save indicator */}
        {(isSaving || lastSavedAt) && (
          <div className={`mb-4 flex items-center justify-center`}>
            <div className={`inline-flex items-center px-4 py-2 ${theme.cards.simple} ${theme.radius.full}`}>
              {isSaving ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  <span className={theme.text.neutral}>Saving your progress...</span>
                </>
              ) : lastSavedAt ? (
                <>
                  <svg className="h-4 w-4 mr-2 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className={theme.text.neutral}>Saved {lastSavedAt.toLocaleTimeString()}</span>
                </>
              ) : null}
            </div>
          </div>
        )}

        {/* Current Section with enhanced styling */}
        <div className={`${theme.cards.default} p-6 sm:p-8 mb-6 animate-fadeIn`}>
          <CurrentSection
            data={formData as any}
            preExistingData={formData.preExistingConditions || {}}
            familyHistoryData={formData.familyMedicalHistory || {}}
            handleChange={handleFieldUpdate}
            isEditMode={true}
            comments={[]}
            clientId={client?.id}
            author="client"
            onFieldChange={handleFieldUpdate}
            gender={formData.personalInfo?.gender || ''}
            clientName={formData.personalInfo?.fullName || ''}
          />
        </div>

        {/* Navigation Buttons */}
        <div className={`${theme.cards.elevated} p-6`}>
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className={`
                group px-6 py-3 ${theme.radius.large} font-medium 
                transition-all ${theme.animations.duration}
                ${currentStep === 0
                  ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                  : `${theme.backgrounds.neutralCard} ${theme.text.neutral} hover:${theme.backgrounds.neutral} hover:scale-105 hover:${theme.shadows.medium}`
                }
              `}
            >
              <div className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </div>
            </button>

            <div className="flex gap-4 flex-1 sm:flex-initial">
              <button
                type="button"
                onClick={() => saveSection()}
                disabled={isSubmitting || !hasUnsavedChanges}
                className={`
                  group flex-1 sm:flex-initial px-6 py-3 
                  bg-neutral-600 text-white ${theme.radius.large} font-medium 
                  hover:bg-neutral-700 hover:scale-105 
                  transition-all ${theme.animations.duration}
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  ${theme.shadows.small} hover:${theme.shadows.medium}
                `}
              >
                <div className="flex items-center justify-center">
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Save Progress
                </div>
              </button>
              
              <button
                type="button"
                onClick={handleNext}
                disabled={isSubmitting}
                className={`
                  group flex-1 sm:flex-initial px-6 py-3 
                  ${theme.backgrounds.gradientPrimary} text-white 
                  ${theme.radius.large} font-medium 
                  hover:scale-105 hover:${theme.shadows.large}
                  transition-all ${theme.animations.duration}
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  ${theme.shadows.medium}
                  relative overflow-hidden
                `}
              >
                <div className="relative z-10 flex items-center justify-center">
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      {currentStep === formSections.length - 1 ? (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Complete Journey
                        </>
                      ) : (
                        <>
                          Continue
                          <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            </div>
          </div>

          {/* Enhanced save reminder */}
          <div className={`text-center mt-6 ${theme.text.neutralLight}`}>
            <div className="flex items-center justify-center text-sm">
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Your wellness information is automatically saved as you progress
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MultiStepIntakeFormV2: React.FC = () => {
  return (
    <IntakeFormProvider>
      <MultiStepIntakeFormContent />
    </IntakeFormProvider>
  );
};

export default MultiStepIntakeFormV2;